// src/components/agent/parts/ApprovalCard.js
//
// The one-off external-effect approval card (email spec §7.15). It renders ONE
// card per durable approval record, describing every ASK call the agent parked
// on — recipients, subject, attachment names, and the Email Outputs of a
// Function run — with "Approve once" / "Deny" buttons.
//
// The card is a pure view of the model built by session/approvalHydration.js.
// It never reconstructs, guesses, or falls back: a record the hydrator marked
// non-approvable shows that bounded reason with both controls disabled, and
// machine identifiers (contact UUIDs, tool_use ids) are never displayed.
//
// Approvals are strictly one-off: there is deliberately NO "always allow"
// control anywhere in this component. Both buttons disable the instant one is
// clicked (a local guard plus the parent-supplied decisionStatus) so a decision
// cannot be double-submitted, and the card shows an approved/denied outcome once
// the decision is durable.
import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Stack, Typography, Button, Chip, CircularProgress, Collapse } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SubjectIcon from '@mui/icons-material/Subject';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const DECIDED_LABELS = {
  approved: 'Approved',
  denied: 'Denied',
  closed: 'Decision recorded',
};

const RecipientLines = ({ recipients }) => (
  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
    <PersonOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', mt: '2px' }} />
    <Box>
      {recipients.map((recipient, i) => (
        <Typography key={i} variant="body2" sx={{ color: 'text.primary' }}>
          {recipient}
        </Typography>
      ))}
    </Box>
  </Box>
);

const SubjectLine = ({ subject }) => (
  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
    <SubjectIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
    <Typography variant="body2" sx={{ color: 'text.primary' }}>
      {subject}
    </Typography>
  </Box>
);

const Attachments = ({ names }) => (
  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
    {names.map((name, i) => (
      <Chip
        key={`${name}-${i}`}
        icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
        label={name}
        size="small"
        variant="outlined"
        sx={{ maxWidth: '100%' }}
      />
    ))}
  </Stack>
);

// The files a direct agent email attaches. Each arrives as its whole path
// within the agent's workspace ("outputs/drafts/report.xlsx"), because two
// reports with one name in different folders are different files and the user
// is approving one of them. The name leads and the folder follows in lighter
// text. A file at the top of the workspace has no folder to show. A long name
// is cut off at the end, which is where the folder is, so the whole path is
// also the chip's tooltip.
const WorkspaceFileAttachments = ({ paths }) => (
  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
    {paths.map((path, i) => {
      const lastSlash = path.lastIndexOf('/');
      const fileName = path.slice(lastSlash + 1);
      const folder = lastSlash === -1 ? '' : path.slice(0, lastSlash);
      return (
        <Chip
          key={`${path}-${i}`}
          title={path}
          icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
          label={(
            <>
              {fileName}
              {folder && (
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.75 }}>
                  in {folder}
                </Typography>
              )}
            </>
          )}
          size="small"
          variant="outlined"
          sx={{ maxWidth: '100%' }}
        />
      );
    })}
  </Stack>
);

// The email's text, collapsed behind a chip: the card is the only human gate
// before the email leaves under the company's name, so the words it carries
// must be reviewable without crowding the recipients and subject.
const EmailBody = ({ body }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 0.75 }}>
      <Chip
        icon={open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        label={open ? 'Hide email' : 'Show email'}
        size="small"
        variant="outlined"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      />
      <Collapse in={open} unmountOnExit>
        <Paper
          variant="outlined"
          sx={{ mt: 1, p: 1.25, backgroundColor: 'background.paper' }}
        >
          <Typography
            variant="body2"
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'inherit',
              color: 'text.primary',
            }}
          >
            {body}
          </Typography>
        </Paper>
      </Collapse>
    </Box>
  );
};

// A direct agent email. The card names the people it will go to, not their
// addresses: a contact is one person whose address can be corrected in
// Settings, and the email goes to the address the contact holds when it is
// sent. The contact identifiers are never shown either. A record parked
// before the body joined the summary has no body to show.
const SendEmailSummary = ({ summary }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
      Send email
    </Typography>
    <RecipientLines recipients={summary.recipients.map((recipient) => recipient.displayName)} />
    <SubjectLine subject={summary.subject} />
    <WorkspaceFileAttachments paths={summary.attachments} />
    {summary.body ? <EmailBody body={summary.body} /> : null}
  </Box>
);

// A Function run: the Function's name, then every Email Output it will send.
const FunctionRunSummary = ({ summary }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
      Run function: {summary.functionName}
    </Typography>
    <Stack spacing={1} sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
      {summary.outputs.map((output, i) => (
        <Box key={i}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {output.nodeName} ({output.outputType})
          </Typography>
          <RecipientLines recipients={output.recipients} />
          <SubjectLine subject={output.subject} />
          <Attachments names={output.attachments} />
        </Box>
      ))}
    </Stack>
  </Box>
);

// What a decided approval covered: the Function's name when it ran one,
// otherwise the tool name. Several ASK calls in one cycle read as a list.
const decidedSubject = (askCalls) => (
  askCalls
    .map((call) => (
      call.approvalSummary?.tool === 'run_company_data_function'
        ? (call.approvalSummary.functionName || call.toolName)
        : (call.approvalSummary?.tool || call.toolName)
    ))
    .filter(Boolean)
    .join(', ')
);

const CallSummary = ({ call }) => {
  if (call.approvalSummary?.tool === 'send_email') {
    return <SendEmailSummary summary={call.approvalSummary} />;
  }
  if (call.approvalSummary?.tool === 'run_company_data_function') {
    return <FunctionRunSummary summary={call.approvalSummary} />;
  }
  return null;
};

/**
 * part: the hydrated approval model, decorated by the session merge with
 *   { decisionStatus, onDecide }.
 * decisionStatus: 'pending' | 'submitting' | 'approved' | 'denied' | 'closed' | 'error'
 * onDecide(decision): 'approved' | 'denied' — always covers the complete ASK set.
 *
 * readOnly renders a historical/terminal card with no interactive controls.
 */
function ApprovalCard({ part, readOnly = false }) {
  const { askCalls = [], error: recordError = null, decisionStatus = 'pending', onDecide } = part || {};
  const [localPending, setLocalPending] = useState(null);
  // Synchronous guard: block a second click even before the disable re-renders,
  // so a decision can never be double-submitted from one card.
  const submittedRef = useRef(false);

  // A parent-reported error re-opens the card so the user can retry the
  // one-off decision; a terminal decision keeps the local guard latched.
  useEffect(() => {
    if (decisionStatus === 'error') {
      submittedRef.current = false;
      setLocalPending(null);
    }
  }, [decisionStatus]);

  const decidedLabel = DECIDED_LABELS[decisionStatus] || null;
  // Show submitting the moment either the local guard or the parent says so.
  const submitting = !decidedLabel && (decisionStatus === 'submitting' || localPending != null);
  const locked = readOnly || !!recordError || !!decidedLabel || submitting;

  const handle = (decision) => {
    if (locked || submittedRef.current) return;  // one outstanding decision only
    submittedRef.current = true;
    setLocalPending(decision);                    // disable both buttons synchronously
    onDecide?.(decision);
  };

  // A decided approval collapses to one line: the outcome plus what it covered.
  // The full detail belongs to the moment of deciding, not to thread history.
  // Collapsing on the local click too — the decision is only durably confirmed
  // when the turn ends, but the user has already made it, so the card should
  // not keep occupying the thread until then. An error re-opens it (above).
  const settledLabel = decidedLabel || DECIDED_LABELS[localPending] || null;
  if (settledLabel && !recordError) {
    const outcome = decidedLabel ? decisionStatus : localPending;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5 }}>
        {outcome === 'approved' ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
        ) : (
          <BlockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {settledLabel}
          {decidedSubject(askCalls) ? `: ${decidedSubject(askCalls)}` : ''}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        width: '100%',
        borderColor: decidedLabel
          ? (decisionStatus === 'approved' ? 'success.main' : 'divider')
          : 'warning.main',
        backgroundColor: 'background.alt',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <LockOutlinedIcon sx={{ fontSize: 18, color: decidedLabel ? 'text.secondary' : 'warning.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Approval required
        </Typography>
      </Box>

      {recordError ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main', mt: '2px' }} />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {recordError}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
          {askCalls.map((call) => (
            <CallSummary key={call.key} call={call} />
          ))}
        </Stack>
      )}

      {/* Once decided, the card quiets down to the outcome and what it covered. */}
      {!recordError && !decidedLabel && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Approving submits this data outside Decode. This decision applies to this request only.
          </Typography>
        </Box>
      )}

      {decidedLabel ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
          {decisionStatus === 'approved' ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />
          ) : (
            <BlockIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {decidedLabel}
          </Typography>
        </Box>
      ) : (
        !readOnly && (
          <>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={locked}
                onClick={() => handle('approved')}
                startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {submitting ? 'Submitting…' : 'Approve once'}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                disabled={locked}
                onClick={() => handle('denied')}
              >
                Deny
              </Button>
            </Stack>
            {decisionStatus === 'error' && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'error.main' }}>
                Could not submit your decision. Please try again.
              </Typography>
            )}
          </>
        )
      )}
    </Paper>
  );
}

export default ApprovalCard;
